import React, { useContext } from 'react'

import { isFunction } from 'lodash-es'

import { type ChangeTeamStatusValue } from '@/api/changeTeamStatusValue.ts'
import { type ButtonProps } from '@/components/atoms/Button'
import { Checkbox } from '@/components/atoms/Checkbox/Checkbox.tsx'
import { Input } from '@/components/atoms/Input/Input.tsx'
import {
  ConfirmDialog,
  type ConfirmDialogLabels,
  type ConfirmDialogState,
} from '@/components/molecules/ConfirmDialog/ConfirmDialog.tsx'
import {
  SelectTeamStatus,
  type SelectTeamStatusOptions,
} from '@/components/molecules/SelectTeamStatus/SelectTeamStatus.tsx'
import {
  SelectTeamStatusContext,
  SelectTeamStatusProvider,
} from '@/components/molecules/SelectTeamStatus/SelectTeamStatusProvider.tsx'
import { type TeamCarData } from '@/components/molecules/Status'

export interface ConfirmCompleteDialogLabels extends Omit<ConfirmDialogLabels, 'description'> {
  description?: string | ((teamNumber: string) => string)
  donationPlaceholder: string
  receipt: string
}

export interface ConfirmCompleteDialogOptions extends SelectTeamStatusOptions {}

export interface ConfirmCompleteDialogState extends ConfirmDialogState {}

export interface ConfirmCompleteDialogData {
  team: TeamCarData & { usingMobileApp: boolean }
}

export type TeamStatus = ChangeTeamStatusValue

export interface ConfirmCompleteDialogAction {
  onConfirm: (teamStatus: TeamStatus, donation: { value: number; hasReceipt: boolean }) => void
}

export interface ConfirmCompleteDialogProps
  extends
    React.HtmlHTMLAttributes<HTMLDivElement>,
    ConfirmCompleteDialogData,
    ConfirmCompleteDialogState,
    ConfirmCompleteDialogAction {
  variant?: ButtonProps['variant']
  labels: ConfirmCompleteDialogLabels
  options: ConfirmCompleteDialogOptions
}

export const ConfirmDialogWithDonationAndSelectTeamStatus = React.forwardRef<
  HTMLDivElement,
  ConfirmCompleteDialogProps
>(({ labels, options, team, onConfirm, ...props }, ref) => {
  const { teamStatus } = useContext(SelectTeamStatusContext)
  const [state, setState] = React.useState({
    donation: '',
    hasReceipt: false,
  })
  const description = isFunction(labels.description) ? labels.description(team.carNumber) : labels.description

  return (
    <ConfirmDialog
      labels={{ ...labels, description }}
      onOpenChange={(value) => {
        if (!value) setState({ donation: '', hasReceipt: false })
      }}
      onConfirmDisabled={!state.donation || !teamStatus}
      onConfirm={() => {
        onConfirm(teamStatus!, { value: Number(state.donation), hasReceipt: state.hasReceipt })
        setState({ donation: '', hasReceipt: false })
      }}
      {...props}
      ref={ref}>
      <>
        <div className="flex items-center gap-2.5">
          <Input
            placeholder={labels.donationPlaceholder}
            type="number"
            step="0.01"
            min={0}
            className="w-44"
            value={state.donation}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setState((value) => ({ ...value, donation: event.target.value }))
            }}
          />
          <Checkbox
            id="receipt"
            checked={state.hasReceipt}
            onCheckedChange={(checked) => {
              setState((value) => ({ ...value, receipt: !!checked }))
            }}
          />
          <label htmlFor="receipt" className="text-sm peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {labels.receipt}
          </label>
        </div>
        <SelectTeamStatus
          team={team}
          teamStatusOptions={options.teamStatusOptions}
          teamAvailableInRegionOptions={options.teamAvailableInRegionOptions}
        />
      </>
    </ConfirmDialog>
  )
})

export const ConfirmCompleteDialog = React.forwardRef<HTMLDivElement, ConfirmCompleteDialogProps>(
  ({ team, ...props }, ref) => (
    <SelectTeamStatusProvider defaultValue={team.usingMobileApp ? 'gps' : 'region'}>
      <ConfirmDialogWithDonationAndSelectTeamStatus team={team} {...props} ref={ref} />
    </SelectTeamStatusProvider>
  )
)
